/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useContext, useState, useEffect } from 'react'
import ProductShelfSkeleton from 'src/components/skeletons/ProductShelfSkeleton'
import { useCart } from '@faststore/sdk'
import type { CartItem } from 'src/sdk/cart/validate'
import ProductCard from 'src/components/product/ProductCardV2'
import { getInstallment } from 'src/utils/formatted'
import { useUI } from 'src/sdk/ui'
import type { ProductItem } from 'src/components/product-page/context/types/product.type'
import { ProductContext } from 'src/components/product-page/context'

import ShelfBoughtTogetherFooter from './ShelfBoughtTogetherFooter'
import './shelf-bought-together.scss'
import type { ActiveItem, Product, Items, Images } from './types'

type BoughtTogetherProps = {
  allItems: Product[]
  currentItem: Product
}

function generateNewActiveItem(setedItem: ActiveItem, currentItem: Product) {
  const currentItemSku = currentItem.items.find(
    (item: Items) => item.itemId === setedItem.itemId
  )

  const offerObject = {
    ...currentItemSku?.offerDefault,
    availability:
      setedItem.quantity > 0
        ? 'https://schema.org/InStock'
        : 'https://schema.org/OutOfStock',
    listPrice: setedItem.listedPrice,
    price: setedItem.price,
    quantity: setedItem.quantity,
    seller: {
      identifier: setedItem.sellerId,
    },
  }

  const offers = {
    ...currentItemSku?.offerDefault,
    lowPrice: setedItem.price,
    offers: [offerObject],
  }

  return {
    ...currentItem,
    productID: setedItem.itemId,
    sku: setedItem.itemId,
    name: setedItem.name,
    offers,
    // slug: currentItem.slug.replace(currentItem.productID, setedItem.itemId),
  }
}

function generateCartItems(
  currentProduct: Product,
  selectedSku: ProductItem,
  itemsActive: Product[]
) {
  currentProduct.sku = selectedSku.itemId

  const productBuyTogether = [currentProduct, ...itemsActive]
  const itemsFinally = [] as any[]

  productBuyTogether.forEach((item) => {
    if (item?.sku) {
      const itemFinally = item.items.find(
        (itemActive: Items) => itemActive.itemId === item.sku
      )

      itemsFinally.push({ productData: item, skuData: itemFinally })
    } else {
      itemsFinally.push({ productData: item, skuData: item.items[0] })
    }
  })

  return itemsFinally.map((currentItem) => {
    const customDataItems = currentItem.productData.items

    const listedPrice = customDataItems?.find(
      (customDataItem: Items) =>
        customDataItem.sellers[0].commertialOffer.ListPrice &&
        customDataItem.sellers[0].commertialOffer.ListPrice > 0
    )?.sellers[0].commertialOffer.ListPrice

    const currentVariation = customDataItems?.find(
      (customDataItem: Items) =>
        customDataItem.itemId === currentItem.skuData.itemId
    )?.variations

    const cartItem: CartItem = {
      id: currentItem.skuData.itemId,
      quantity: 1,
      price: currentItem.skuData.offerDefault.price,
      listPrice: listedPrice ?? currentItem.skuData.offerDefault.price,
      seller: {
        identifier: currentItem.skuData.sellers[0].sellerId,
      },
      itemOffered: {
        additionalProperty: [],
        brand: {
          name: currentItem.productData.brand ?? ' ',
        },
        gtin: currentItem.skuData.referenceId[0].Value,
        image: currentItem.skuData.images.map((image: Images) => ({
          url: image.imageUrl,
          alternateName: image.imageText,
        })),
        isVariantOf: {
          productGroupID: currentItem.productData.productId,
          name: currentItem.productData.productName,
        },
        variant: currentVariation,
        name: currentItem.skuData.name,
        sku: currentItem.skuData.itemId,
        slug: currentItem.productData.linkText,
      },
    }

    return cartItem
  })
}

export function ShelfBoughtTogether({
  allItems,
  currentItem,
}: BoughtTogetherProps) {
  const { setCart, id: cartId, items: itemsCart } = useCart()
  const { openMinicart } = useUI()

  const { currentSku, specificationNotSelected, setNotification } =
    useContext(ProductContext)

  const [dataLoaded, setDataLoaded] = useState(false)
  const [itemsActive, setItemsActive] = useState<Product[]>(
    allItems?.slice(0, 2) ?? []
  )

  useEffect(() => {
    if (allItems.length > 0) {
      setItemsActive(allItems?.slice(0, 2) ?? [])
      setDataLoaded(true)
    }
  }, [allItems])

  const [nextActiveItemIndex, setNextActiveItemIndex] = useState(2)
  const productBuyTogether = [currentItem, ...itemsActive]
  const itemsFinally = [] as any[]

  const currentItemSku = currentItem.items.filter(
    (item) => item.itemId === currentSku.itemId
  )

  itemsFinally.push({ skuData: currentItemSku[0] })
  productBuyTogether.slice(1, 3).forEach((item) => {
    if (item?.sku) {
      itemsFinally.push({
        skuData: item.items.find(
          (itemActive: Items) => itemActive.itemId === item.sku
        ),
      })
    } else {
      itemsFinally.push({ skuData: item.items[0] })
    }
  })

  const totalPriceItemsCalc = itemsFinally?.reduce((acc: number, item) => {
    const price: number = item?.skuData?.offerDefault?.price ?? 0

    return acc + price
  }, 0)

  const [decimalValue, centsValue] = String(totalPriceItemsCalc).split('.')
  const inst = totalPriceItemsCalc && getInstallment(totalPriceItemsCalc)

  const handleChangedItemArraySKU = useCallback(
    (setedItem: ActiveItem) => {
      const itemsArray = [...itemsActive]

      if (Object.keys(setedItem).length === 0 || !setedItem) {
        return
      }

      const arrayIndex = itemsArray.findIndex(
        (arrayItem) =>
          arrayItem.items?.find(
            (itemCustomData: Items) =>
              itemCustomData.itemId === setedItem.itemId
          )?.itemId === setedItem.itemId
      )

      const selectedItem = itemsArray[arrayIndex]

      if (selectedItem === undefined) {
        return
      }

      const newActiveItem = generateNewActiveItem(setedItem, selectedItem)

      itemsArray[arrayIndex] = newActiveItem
      setItemsActive(itemsArray)
    },
    [itemsActive]
  )

  function handleDeleteCard(productId: string) {
    if (itemsActive.length <= 1) {
      return
    }

    setItemsActive((prevValues) =>
      prevValues.filter((product) => product.productId !== productId)
    )
  }

  function handleReloadCard(productId: string) {
    const currentProductIndex = itemsActive.findIndex(
      (product) => product.productId === productId
    )

    let nextIndex = nextActiveItemIndex

    // Checks next product index that is not visible
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    for (const _item of allItems) {
      // Reset the index to the beginning when already passed throw all items
      if (nextIndex > allItems?.length - 1) {
        nextIndex = 0
      }

      const nextProduct = allItems[nextIndex]
      const isItemVisible = itemsActive?.some(
        (product) => product.productId === nextProduct.productId
      )

      if (isItemVisible) {
        nextIndex += 1
      } else {
        break
      }
    }

    setItemsActive((prevValues) => {
      const nextProduct = allItems[nextIndex]

      prevValues[currentProductIndex] = nextProduct

      return prevValues
    })
    setNextActiveItemIndex(nextIndex + 1)
  }

  async function handleAddItemsToCart() {
    if (!currentItem) {
      return
    }

    const specificationName = () => {
      if (
        specificationNotSelected !== false &&
        specificationNotSelected !== null
      ) {
        return specificationNotSelected?.field.name.toLowerCase()
      }

      return ''
    }

    if (specificationNotSelected) {
      setNotification({
        message: `Você precisa escolher um(a) ${specificationName()}`,
        type: 'variations',
      })

      return
    }

    const cartItems = generateCartItems(currentItem, currentSku, itemsActive)

    setCart({
      id: cartId,
      items: [...itemsCart, ...cartItems],
    })

    openMinicart()
  }

  // moved this check here to make sonar happy
  function checkItemsActiveLength(length: number) {
    return length === 1 ? 'last-item-expanded' : ''
  }

  if (!allItems?.length || !currentItem) {
    return <ProductShelfSkeleton loading />
  }

  function checkQuantity() {
    if (currentItem) {
      return (
        currentItem?.items[0].sellers[0]?.commertialOffer?.AvailableQuantity > 0
      )
    }

    return false
  }

  return (
    <>
      {checkQuantity() && dataLoaded ? (
        <ProductShelfSkeleton loading={!allItems?.length || !currentItem}>
          <h2 className="title-section md:text-center uppercase font-bold italic text-neutral10 mb-11">
            APROVEITE E COMPRE JUNTO
          </h2>
          <ul className="bought-together-list">
            <li>
              <ProductCard
                product={currentItem}
                index={Number(currentItem.itemId)}
                skuItem={currentItemSku[0]}
                stylesBoughtTogether
                isBoughtTogether
                notSelector
              />

              <p className="text-blue font-bold text-center uppercase mt-1 pp:text-xs xs:text-sm">
                você está vendo este produto
              </p>
            </li>
            {itemsActive.map((product, idx: number) => (
              <li key={product.productId}>
                <ProductCard
                  product={product} // need to convert object to ProductSummary_ProductFragment, aside from the typing the code works as intended
                  index={idx}
                  handleChangedItemSKU={handleChangedItemArraySKU}
                  isBoughtTogether
                  stylesBoughtTogether
                  itemsActive={itemsActive}
                />
                <ShelfBoughtTogetherFooter
                  onDeleteCard={() => handleDeleteCard(product.productId)}
                  onReloadCard={() => handleReloadCard(product.productId)}
                />
              </li>
            ))}
            <li
              className={`${checkItemsActiveLength(
                itemsActive.length
              )} bg-neutral02 p-6 rounded gap-y-2 flex flex-col justify-center`}
            >
              <p className="font-bold italic text-2xl text-deepGray uppercase text-center">
                Adquira os {itemsActive.length + 1} <br />
                produtos por
              </p>
              <p className="flex justify-center align-start gap-x-1">
                <span className="font-bold leading-9 uppercase">R$</span>
                <span className="font-bold text-[2.5rem] uppercase">
                  {decimalValue},
                </span>
                <span className="font-bold leading-9 uppercase">
                  {centsValue?.slice(0, 3)}
                </span>
              </p>
              <p className="text-center">
                ou <strong>{inst && inst.installment}x</strong> de{' '}
                <strong>R$ {inst && inst.installmentPrice}</strong>
              </p>
              <button
                className="bg-vivid-yellow w-full uppercase py-2.5 px-5"
                data-store-buy-button
                name="buy together"
                aria-label="buy together"
                onClick={() => handleAddItemsToCart()}
              >
                Comprar junto
              </button>
            </li>
          </ul>
        </ProductShelfSkeleton>
      ) : (
        <></>
      )}
    </>
  )
}
